import { LogInIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { PageLoading } from "../components/ui/loading";
import { ApiError, apiFetch } from "../lib/api";

/**
 * Wejście z zaproszenia — jedyna droga do pokoju bez konta.
 *
 * Kod sprawdzamy wzorcem **przed** jakimkolwiek wywołaniem API. Skanery
 * dobijające losowe adresy generowałyby inaczej ruch do bazy przy każdym trafieniu
 * w `/z/<cokolwiek>`, a to jedyna trasa aplikacji dostępna bez uwierzytelnienia.
 */
const CODE_PATTERN = /^[a-z0-9]{6,16}$/;

interface InviteInfo {
  code: string;
  roomSlug: string;
  roomName: string;
  role: "viewer" | "controller";
  expiresAt: string | null;
  usesLeft: number | null;
}

export function InviteScreen() {
  const { code = "" } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!CODE_PATTERN.test(code)) {
      setError("Ten odnośnik nie wygląda na zaproszenie");
      return;
    }
    let cancelled = false;
    apiFetch<InviteInfo>(`/invites/${code}`)
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Nie udało się sprawdzić zaproszenia");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  async function join(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<{ roomSlug: string }>(`/invites/${code}/join`, {
        method: "POST",
        body: JSON.stringify({ nickname: nickname.trim() }),
      });
      navigate(`/pokoj/${result.roomSlug}`, { replace: true });
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się wejść do pokoju");
    } finally {
      setBusy(false);
    }
  }

  if (error !== null && info === null) {
    return (
      <Card className="mx-auto max-w-md border-destructive/40 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg text-destructive">Zaproszenie nieważne</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (info === null) return <PageLoading label="Sprawdzam zaproszenie…" />;

  return (
    <Card className="mx-auto max-w-md">
      <CardHeader>
        <CardTitle className="text-xl">{info.roomName}</CardTitle>
        <CardDescription>
          {info.role === "controller"
            ? "To zaproszenie pozwala też prowadzić odtwarzanie."
            : "To zaproszenie pozwala oglądać i pisać na czacie."}
        </CardDescription>
        <div className="mt-1 flex flex-wrap gap-2">
          <Badge variant={info.role === "controller" ? "accent" : "secondary"}>
            {info.role === "controller" ? "Może prowadzić" : "Widz"}
          </Badge>
          {info.usesLeft !== null && (
            <Badge variant="outline">Pozostałe wejścia: {info.usesLeft}</Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <form onSubmit={(e) => void join(e)} className="flex flex-col gap-3">
          <label htmlFor="nickname" className="text-xs text-muted-foreground">
            Jak mamy Cię nazywać
          </label>
          <Input
            id="nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Twój nick"
            required
            minLength={2}
            maxLength={24}
          />

          <Button type="submit" variant="hero" disabled={busy || nickname.trim().length < 2}>
            <LogInIcon />
            {busy ? "Wchodzę…" : "Wejdź do pokoju"}
          </Button>

          {error !== null && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        <p className="text-xs text-muted-foreground">
          Bez konta zapisujemy tylko nick na czas ważności zaproszenia. Postęp oglądania i
          biblioteka wymagają zalogowania.
        </p>
      </CardContent>
    </Card>
  );
}
